import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, AlertTriangle, Lightbulb, Check, Shield, RotateCcw,
} from 'lucide-react';

export default function OutputNarrativeSection({ narrative, narrativeLoading, generateNarrative }) {
  return (
    <div className="space-y-6" data-testid="section-narrative">
      {narrativeLoading && (
        <div className="flex flex-col items-center gap-3 py-16" data-testid="narrative-loading">
          <div className="w-10 h-10 border-3 border-violet-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Generating AI analysis of your estimate...</p>
        </div>
      )}

      {!narrative && !narrativeLoading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Sparkles className="w-10 h-10 text-violet-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">Generate an AI-powered analysis of risks, opportunities, and benefits.</p>
            <Button onClick={generateNarrative} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="generate-narrative-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Generate AI Analysis
            </Button>
          </CardContent>
        </Card>
      )}

      {narrative && (
        <>
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-[#0F2040] mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" /> Executive Summary
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed" data-testid="narrative-summary">{narrative.executive_summary}</p>
            </CardContent>
          </Card>

          {narrative.risks?.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-[#0F2040] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" /> Risks
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {narrative.risks.map((r, i) => (
                  <Card key={`risk-${r.title || i}`} className={`border-l-4 ${r.severity === 'high' ? 'border-l-red-500' : r.severity === 'medium' ? 'border-l-amber-400' : 'border-l-slate-300'}`} data-testid={`narrative-risk-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-1.5">
                        <h3 className="font-semibold text-sm text-slate-800">{r.title}</h3>
                        <Badge variant="outline" className={`text-[10px] ml-2 ${r.severity === 'high' ? 'text-red-600 border-red-200 bg-red-50' : r.severity === 'medium' ? 'text-amber-600 border-amber-200 bg-amber-50' : 'text-slate-500'}`}>{r.severity}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed mb-2">{r.description}</p>
                      {r.mitigation && (
                        <div className="flex gap-1.5 p-2 rounded bg-emerald-50 border border-emerald-100">
                          <Shield className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-emerald-700 leading-relaxed">{r.mitigation}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {narrative.opportunities?.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-[#0F2040] mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-blue-500" /> Opportunities
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {narrative.opportunities.map((o, i) => (
                  <Card key={`opp-${o.title || i}`} className="border-l-4 border-l-blue-400" data-testid={`narrative-opportunity-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-1.5">
                        <h3 className="font-semibold text-sm text-slate-800">{o.title}</h3>
                        {o.impact && <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200 bg-blue-50 ml-2">{o.impact}</Badge>}
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{o.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {narrative.benefits?.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-[#0F2040] mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" /> Benefits
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {narrative.benefits.map((b, i) => (
                  <Card key={`benefit-${b.title || i}`} className="border-l-4 border-l-emerald-400" data-testid={`narrative-benefit-${i}`}>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm text-slate-800 mb-1">{b.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {narrative.recommendations?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Recommendations</CardTitle></CardHeader>
              <CardContent>
                <ol className="space-y-2">
                  {narrative.recommendations.map((rec, i) => (
                    <li key={`rec-${i}-${(rec || '').slice(0, 30)}`} className="flex gap-3 text-sm" data-testid={`narrative-rec-${i}`}>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="text-slate-700 leading-relaxed pt-0.5">{rec}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          {narrative.timeline_guidance && (
            <Card className="border-l-4 border-l-slate-400">
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm text-[#0F2040] mb-2">Timeline Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-testid="narrative-timeline">{narrative.timeline_guidance}</p>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-center">
            <Button variant="outline" onClick={generateNarrative} disabled={narrativeLoading} data-testid="regenerate-narrative-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Regenerate Analysis
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
